import React, { Fragment } from "react";
import PropTypes from "prop-types";
import Form from "../../common/Form";
import Fieldset from "../../common/Fieldset";
import TextInput from "../../common/TextInput";
import TextAreaInput from "../../common/TextAreaInput";
import NumberInput from "../../common/NumberInput";
import SelectInput from "../../common/SelectInput";
import DateInput from "../../common/DateInput";
import RadioButtonGroup from "../../common/RadioButtonGroup";
import JaNeeRadioButtonGroup from "../../common/JaNeeRadioButtonGroup";
import TrueFalseRadioButtonGroup from "../../common/TrueFalseRadioButtonGroup";
import DeelnamePrijzenComponent from "./parts/DeelnamePrijzenComponent";
import PlaatsPostcodeComponent from "./parts/PlaatsPostcodeComponent";
import BerekenXYComponent from "./parts/BerekenXYComponent";
import GemeenteComponent from "./parts/GemeenteComponent";
import LusComponent from "./parts/LusComponent";
import { Landen, TochtFrequentieTypes, TochtRedenVanStatus, TochtTypeIds } from "../../../constants/DomainConstants";
import Row from "../../common/Row";
import Col from "../../common/Col";
import Confirm from "../../common/Confirm";
import MapView from "../../common/map/MapView";
import { StartplaatsManualText } from "../../common/map/manualTexts";
import * as types from "../../../constants/ComponentConstants";
import { getFilesComponentProps } from "../../common/FilesHelperUtil";
import Files from "../../common/Files";
import Table from "../../common/Table";
import ValidationGroup from "../../common/ValidationGroup";
import { getTochtTypeText } from "../../../util/tochtTypeHelper";

const getDeelnemersSoort = tochtTypeId => {
	switch (tochtTypeId) {
		case TochtTypeIds.GEMOTORISEERDE_TOCHT:
			return " (voertuigen)";
		case TochtTypeIds.MENNEN:
			return " (menners)";
		case TochtTypeIds.MUSHING:
			return " (mushers)";
		default:
			return "";
	}
};

const UpdateTochtForm = props => {
	const {
		model,
		onChange,
		onChangeRadio,
		onChangeDeelnamePrijs,
		onRemoveDeelnamePrijs,
		onChangeExtraOndersteuning,
		onAddDeelnamePrijs,
		onAddGemeente,
		onChangeStartplaats,
		onAddLus,
		onChangeLus,
		onRemoveLus,
		onChangeGemeente,
		onRemoveGemeente,
		onGetGeoCoordinatesStart,
		onSave,
		landen,
		postcodes,
		gemeenten,
		hoofdgemeenten,
		getError,
		disabled,
		validationMessageType,
		showAnbPopup,
		onConfirmAnbPopup,
		osmBaseLayerUrl,
		onMarkerDragEnd,
		mapId,
		draaiboekFileTypes,
		color,
		onDrop,
		onDelete,
		fileComponents,
		downloadFileUrl
	} = props;

	function handleOnChangeDeelnamePrijs(index) {
		return function (event) {
			onChangeDeelnamePrijs(index, event);
		};
	}

	function handleOnRemoveDeelnamePrijs(index) {
		return function (event) {
			onRemoveDeelnamePrijs(index, event);
		};
	}

	function handleOnChangeLus(index) {
		return function (event) {
			onChangeLus(index, event);
		};
	}

	function handleOnRemoveLus(index) {
		return function (event) {
			onRemoveLus(index, event);
		};
	}

	function handleOnChangeGemeente(index) {
		return function (event) {
			onChangeGemeente(index, event);
		};
	}

	function handleOnRemoveGemeente(index) {
		return function (event) {
			onRemoveGemeente(index, event);
		};
	}

	const tochtTypeText = getTochtTypeText();

	const hasStep4Status =
		model.redenVanStatus === TochtRedenVanStatus.DoorgestuurdTerGoedkeuring;

	const hasFinalStatus =
		model.redenVanStatus === TochtRedenVanStatus.DoorgestuurdTerGoedkeuring;

	const isStartplaatsInBelgie = model.startplaatsLand === Landen.BELGIE;
	const isAankomstplaatsInBelgie = model.aankomstLand === Landen.BELGIE;

	const mapCenter = (model.startplaatsBreedtegraad && model.startplaatsLengtegraad) ? [model.startplaatsBreedtegraad, model.startplaatsLengtegraad] : ['51.0132', '4.3554'];
	const markerStart = (model.startplaatsBreedtegraad && model.startplaatsLengtegraad) ? { size: 'medium', color: types.COLOR_BLUE, lat: model.startplaatsBreedtegraad, long: model.startplaatsLengtegraad, id: model.id } : null;

	const radiobuttongroupFrequentie = [
		{ value: TochtFrequentieTypes.EENMALIG, label: "Eenmalig" },
		{ value: TochtFrequentieTypes.JAARLIJKS, label: "Jaarlijks" }
	];

	const volledig = !model.validatiestatusses.filter(x => x.volledig === false).length;

	const anbPopupText = volledig ? "Jouw dossier is klaar voor ANB en zal overgemaakt worden" : "Jouw dossier is nog niet klaar voor ANB en zal niet overgemaakt worden.";

	const vorigeEditie =
		model["frequentie"] > TochtFrequentieTypes.EENMALIG && model["eersteEditie"] === false ? (
			<JaNeeRadioButtonGroup
				label={`Identieke ${tochtTypeText.TochtTypeSingular} als vorige editie`}
				name="identiekeVeldtoertochtAlsVorigJaar"
				value={model["identiekeVeldtoertochtAlsVorigJaar"]}
				onChange={onChangeRadio}
				size={6}
				horizontal
				labelInside
				disabled={disabled || hasStep4Status || hasFinalStatus}
				validationMessage={getError("identiekeVeldtoertochtAlsVorigJaar")}
				validationMessageType={validationMessageType}
			/>
		) : null;

	const frequentiePanel =
		model["frequentie"] > TochtFrequentieTypes.EENMALIG ? (
			<Row>
				<JaNeeRadioButtonGroup
					label="Eerste editie"
					name="eersteEditie"
					value={model["eersteEditie"]}
					onChange={onChangeRadio}
					size={6}
					horizontal
					labelInside
					disabled={disabled || hasStep4Status || hasFinalStatus}
					validationMessage={getError("eersteEditie")}
					validationMessageType={validationMessageType}
				/>
				{vorigeEditie}
			</Row>
		) : null;

	const deelnemersSoort = getDeelnemersSoort(model["tochtTypeId"]);

	const werkelijkAantalDeelnemersVorigeEditie =
		model["frequentie"] > TochtFrequentieTypes.EENMALIG && model["eersteEditie"] === false ? (
			<NumberInput
				name="werkelijkAantalDeelnemersVorigeEditie"
				label={`Werkelijk aantal deelnemers${deelnemersSoort} vorige editie`}
				tooltip={`Vul hier het aantal deelnemers${deelnemersSoort} in van de laatste editie`}
				value={model.werkelijkAantalDeelnemersVorigeEditie}
				onChange={onChange}
				noDecimals
				size={6}
				validationMessage={getError("werkelijkAantalDeelnemersVorigeEditie")}
				validationMessageType={validationMessageType}
				disabled={disabled || hasStep4Status || hasFinalStatus}
			/>
		) : null;

	const prijzenErrors = getError("deelnamePrijzen");
	const prijzen = model.deelnamePrijzen.map((deelnamePrijs, index) => {
		const prijsError = prijzenErrors ? prijzenErrors[index] : null;
		const id = "deelnamePrijs_" + index;

		return (
			<DeelnamePrijzenComponent
				key={id}
				omschrijving={deelnamePrijs.omschrijving}
				prijs={deelnamePrijs.prijs}
				minButtonEnabled={model.deelnamePrijzen.length > 1}
				plusButtonEnabled={index === model.deelnamePrijzen.length - 1}
				disabled={disabled || hasStep4Status || hasFinalStatus}
				onPlusButtonClick={onAddDeelnamePrijs}
				onMinButtonClick={handleOnRemoveDeelnamePrijs(index)}
				onChange={handleOnChangeDeelnamePrijs(index)}
				index={index}
				errors={prijsError}
				validationMessageType={validationMessageType}
			/>
		);
	});
	const lusErrors = getError("lussen");

	const hasOnvolledigeLus = lusErrors && Object.keys(lusErrors).some(x => lusErrors[x].isVolledig !== undefined);

	const lussen = model.lussen.map((lus, index) => {
		const lusError = lusErrors ? lusErrors[index] : null;
		const id = "lus_" + index;

		return (
			<LusComponent
				key={id}
				naam={lus.naam}
				minButtonEnabled={model.lussen.length > 1}
				plusButtonEnabled={index === model.lussen.length - 1}
				disabled={disabled || hasStep4Status || hasFinalStatus}
				onPlusButtonClick={onAddLus}
				onMinButtonClick={handleOnRemoveLus(index)}
				onChange={handleOnChangeLus(index)}
				index={index}
				errors={lusError}
				validationMessageType={validationMessageType}
			/>
		);
	});

	const gemErrors = getError("gemeenten");
	const gemeenteComp = model.gemeenten.map((gemeente, index) => {
		const id = "gemeente_" + index;

		return (
			<GemeenteComponent
				key={id}
				gemeente={gemeente}
				hoofdgemeenten={hoofdgemeenten}
				minButtonEnabled={model.gemeenten.length > 1}
				plusButtonEnabled={index == model.gemeenten.length - 1}
				disabled={disabled || model.disableDatumGemeente || hasStep4Status || hasFinalStatus}
				onPlusButtonClick={onAddGemeente}
				onMinButtonClick={handleOnRemoveGemeente(index)}
				onChange={handleOnChangeGemeente(index)}
				index={index}
				validationMessageType={validationMessageType}
			/>
		);
	});

	const ondersteuningOmschrijving = model.extraOndersteuningDoorANBGewenst ? (
		<TextAreaInput
			name="extraOndersteuningDoorANBOmschrijving"
			label="Beschrijf welke ondersteuning van ANB je wenst"
			value={model.extraOndersteuningDoorANBOmschrijving}
			onChange={onChange}
			validationMessage={getError("extraOndersteuningDoorANBOmschrijving")}
			validationMessageType={validationMessageType}
			disabled={disabled || hasStep4Status || hasFinalStatus}
		/>
	) : null;

	const reclameOmschrijving = model.reclameInANBGebied ? (
		<TextAreaInput
			name="omschrijvingGebiedReclame"
			label="Omschrijf in welk gebied de reclame zal geplaatst worden"
			value={model.omschrijvingGebiedReclame}
			onChange={onChange}
			validationMessage={getError("omschrijvingGebiedReclame")}
			validationMessageType={validationMessageType}
			disabled={disabled || hasStep4Status || hasFinalStatus}
		/>
	) : null;

	const heeftAfstandDoorANBGebied = model.lussen && model.lussen.some(lus => lus.routeLooptDoorANBGebied);

	const extraInformatieAgentschappen = model.tochtTeLaatVoorToelatingAgentschappen === true || model.tochtTeLaatVoorToelatingAgentschappen === "true" || !heeftAfstandDoorANBGebied ?
		null :
		(<Fieldset title={"Extra informatie voor het Agentschap voor Natuur en Bos (ANB)"}>
			<JaNeeRadioButtonGroup
				label="Is er extra ondersteuning door ANB nodig?"
				tooltip="Wordt er extra ondersteuning verwacht van ANB zoals het openen van een slagboom, ... Zo ja, geef de gemeente en de naam van het domein."
				name="extraOndersteuningDoorANBGewenst"
				value={model["extraOndersteuningDoorANBGewenst"]}
				onChange={onChangeExtraOndersteuning}
				horizontal
				labelInside
				disabled={disabled || hasStep4Status || hasFinalStatus}
				validationMessage={getError("extraOndersteuningDoorANBGewenst")}
				validationMessageType={validationMessageType}
			/>
			{ondersteuningOmschrijving}
			<JaNeeRadioButtonGroup
				label="Zal er reclame geplaatst worden in een ANB-gebied?"
				tooltip="Wordt er reclame geplaatst in een domein?... Zo ja, geef de gemeente en de naam van het domein en specifieer de soort reclame en de totale oppervlakte hiervan."
				name="reclameInANBGebied"
				value={model["reclameInANBGebied"]}
				onChange={onChangeExtraOndersteuning}
				horizontal
				labelInside
				disabled={disabled || hasStep4Status || hasFinalStatus}
				validationMessage={getError("reclameInANBGebied")}
				validationMessageType={validationMessageType}
			/>
			{reclameOmschrijving}
		</Fieldset>
		);

	const adresAankomstPlaats = !model.aankomstGelijkAanStartPlaats ? (
		<Fragment>
			<Row>
				<TextInput
					name="aankomstNaam"
					label="Naam"
					value={model.aankomstNaam}
					onChange={onChange}
					validationMessage={getError("aankomstNaam")}
					validationMessageType={validationMessageType}
					disabled={disabled || hasFinalStatus}
					tooltip="Naam of korte omschrijving van de aankomstplaats vb Gemeentepark Parking 5"
				/>
			</Row>
			<Row>
				<SelectInput
					name="aankomstLand"
					label="Land"
					size={6}
					items={landen}
					onChange={onChange}
					disabled={disabled || hasStep4Status || hasFinalStatus}
					value={model.aankomstLand}
					validationMessage={getError("aankomstLand")}
					validationMessageType={validationMessageType}
				/>
			</Row>
			<Row>
				<TextInput
					name="aankomstplaatsStraat"
					label="Straat"
					value={model.aankomstplaatsStraat}
					onChange={onChange}
					validationMessage={getError("aankomstplaatsStraat")}
					validationMessageType={validationMessageType}
					disabled={disabled || hasStep4Status || hasFinalStatus}
					tooltip="Controleer de officiële schrijfwijze van de staatnaam op de website van geopunt.be"
					size={8}
				/>
				<TextInput
					name="aankomstplaatsHuisnummer"
					label="Huisnummer"
					value={model.aankomstplaatsHuisnummer}
					onChange={onChange}
					validationMessage={getError("aankomstplaatsHuisnummer")}
					validationMessageType={validationMessageType}
					disabled={disabled || hasStep4Status || hasFinalStatus}
					size={2}
					optional
				/>
				<TextInput
					name="aankomstplaatsBusnummer"
					label="Bus"
					value={model.aankomstplaatsBusnummer}
					onChange={onChange}
					validationMessage={getError("aankomstplaatsBusnummer")}
					validationMessageType={validationMessageType}
					disabled={disabled || hasStep4Status || hasFinalStatus}
					size={2}
					optional
				/>
			</Row>
			<PlaatsPostcodeComponent
				model={model}
				isInBelgie={isAankomstplaatsInBelgie}
				postcodeField="aankomstPostcode"
				gemeenteField="aankomstGemeente"
				postcodeNoBEField="aankomstplaatsPostcodeBuitenland"
				gemeenteNoBEField="aankomstplaatsGemeenteBuitenland"
				postcodes={postcodes}
				gemeenten={gemeenten}
				disabled={disabled || hasStep4Status || hasFinalStatus}
				onChange={onChange}
				getError={getError}
				validationMessageType={validationMessageType}
			/>
		</Fragment>
	) : null;


	const eventScanDraaiboekProps = getFilesComponentProps(
		"Jouw draaiboek coronamaatregelen",
		types.FileComponentNames.DRAAIBOEK_CORONA_MAATREGELEN,
		downloadFileUrl,
		fileComponents,
		disabled,
		onDrop,
		onDelete,
		draaiboekFileTypes,
		true,
		validationMessageType,
		getError("draaiboekCoronaDocumentNames"),
		color,
		6);

	const legendeCustomRows = [
		<Row key={"Startplaats"}>&nbsp;&nbsp;<div className="fa fa-map-marker marker-small marker-blue"></div><div style={{ height: '24px', float: 'right', marginRight: '24px' }}>Startplaats tocht</div></Row>,
		<Row key={""}><img style={{ height: '30px', width: '30px', marginLeft: '-9px' }} src={require('../../../images/map/ANBGebied.png')} />&nbsp;ANB Gebied</Row>
	];

	const verwachtAantalDeelnemersTooltip = "Hier verschijnt het totaal verwacht aantal deelnemers van de tocht op basis van de doorgegeven verwachte aantallen deelnemers per afstand.";

	const overlappingen = model.overlappingen || [];
	const overlappingenSection = hasStep4Status && overlappingen.length ? (
		<Fieldset title={"Overlappingen met andere tochten"}>
			<Row>
				<Col size={12}>
					<p style={{ marginBottom: "10px" }}>Deze tocht loopt gedeeltelijk over dezelfde route als een andere tocht. Als organisator moet je hiervoor geen actie ondernemen. De betrokken gemeenten bekijken dit verder en je wordt door hen gecontacteerd indien nodig.</p>
				</Col>
			</Row>
			<Row>
				<Col size={12}>
					<Table
						headers={["Gemeente", "Type tocht", "Dossiernummer tocht", "Naam tocht", "Organisator"]}
						headerSizes={[15, 15, 21, 26, 23]}
					>
						{overlappingen.map((overlapping, index) => (
							<tr key={"overlapping_" + index}>
								<td>{overlapping.gemeente}</td>
								<td>{overlapping.tochtType}</td>
								<td>{overlapping.dossiernummer}</td>
								<td>{overlapping.tochtNaam}</td>
								<td>{overlapping.organisator}</td>
							</tr>
						))}
					</Table>
				</Col>
			</Row>
		</Fieldset>
	) : null;

	return (
		<Fragment>
			{!showAnbPopup
				||
				<Confirm
					message={anbPopupText}
					title={"Klaar voor ANB?"}
					callback={onConfirmAnbPopup}
					confirmText="OK"
				/>}
			<Form
				name="veldtoertocht"
				title={"Gegevens van de tocht"}
				headerVisible={false}
				leftButtonLabel={"Bewaar"}
				onLeftButtonClick={onSave}
				leftButtonDisabled={disabled}
			>
				{overlappingenSection}
				<Fieldset>
					<TextInput
						name="naam"
						label={`Naam ${tochtTypeText.TochtTypeSingular}`}
						size={6}
						value={model.naam}
						onChange={onChange}
						validationMessage={getError("naam")}
						validationMessageType={validationMessageType}
						disabled={disabled || hasStep4Status || hasFinalStatus}
					/>
					<DateInput
						name="datum"
						label={`Datum ${tochtTypeText.TochtTypeSingular}`}
						size={6}
						value={model.datum}
						onChange={onChange}
						validationMessage={getError("datum")}
						validationMessageType={validationMessageType}
						disabled
					/>
				</Fieldset>
				<Fieldset title={"Frequentie"}>
					<Row>
						<RadioButtonGroup
							name="frequentie"
							value={model["frequentie"]}
							onChange={onChange}
							items={radiobuttongroupFrequentie}
							horizontal
							disabled={disabled || hasStep4Status || hasFinalStatus}
							validationMessage={getError("frequentie")}
							validationMessageType={validationMessageType}
						/>
					</Row>
					{frequentiePanel}
				</Fieldset>
				<Fieldset title={"Aantal deelnemers"}>
					<NumberInput
						name="verwachtAantalDeelnemers"
						label="Verwacht aantal deelnemers totaal"
						value={model.verwachtAantalDeelnemers}
						noDecimals
						size={6}
						validationMessage={getError("verwachtAantalDeelnemers")}
						validationMessageType={validationMessageType}
						tooltip={verwachtAantalDeelnemersTooltip}
						disabled
					/>
					{werkelijkAantalDeelnemersVorigeEditie}
				</Fieldset>
				<Fieldset title={"Inschrijvingen en deelnameprijs"}>
					<Row>
						<JaNeeRadioButtonGroup
							label="Voorinschrijvingen"
							name="voorinschrijvingen"
							value={model["voorinschrijvingen"]}
							optional
							onChange={onChangeRadio}
							size={6}
							horizontal
							labelInside
							disabled={disabled || model.redenVanStatus === TochtRedenVanStatus.InAfwachtigingVanMachtiging}
							validationMessage={getError("voorinschrijvingen")}
							validationMessageType={validationMessageType}
						/>
						<JaNeeRadioButtonGroup
							label="Contactloos btalen mogelijk?"
							name="contactloosBetalenMogelijk"
							value={model["contactloosBetalenMogelijk"]}
							onChange={onChangeRadio}
							size={6}
							horizontal
							labelInside
							disabled={disabled || model.redenVanStatus === TochtRedenVanStatus.InAfwachtigingVanMachtiging}
							validationMessage={getError("contactloosBetalenMogelijk")}
							validationMessageType={validationMessageType}
						/>
					</Row>
					{prijzen}
				</Fieldset>
				<Fieldset
					title={"Startplaats"}
				>
					<Row>
						<TextInput
							name="startplaatsNaam"
							label="Naam"
							value={model.startplaatsNaam}
							onChange={onChange}
							validationMessage={getError("startplaatsNaam")}
							validationMessageType={validationMessageType}
							disabled={disabled || hasFinalStatus}
							tooltip="Naam of korte omschrijving van de startplaats vb Gemeentepark Parking 3"
						/>
					</Row>
					<Row>
						<SelectInput
							name="startplaatsLand"
							label="Land"
							size={6}
							items={landen}
							onChange={onChangeStartplaats}
							disabled
							value={model.startplaatsLand}
							validationMessage={getError("startplaatsLand")}
							validationMessageType={validationMessageType}
						/>
					</Row>
					<Row>
						<TextInput
							name="startplaatsStraat"
							label="Straat"
							value={model.startplaatsStraat}
							onChange={onChangeStartplaats}
							validationMessage={getError("startplaatsStraat")}
							validationMessageType={validationMessageType}
							disabled={disabled || hasStep4Status || hasFinalStatus}
							tooltip="Controleer de officiële schrijfwijze van de staatnaam op de website van geopunt.be"
							size={8}
						/>
						<TextInput
							name="startplaatsHuisnummer"
							label="Huisnummer"
							value={model.startplaatsHuisnummer}
							onChange={onChangeStartplaats}
							validationMessage={getError("startplaatsHuisnummer")}
							validationMessageType={validationMessageType}
							disabled={disabled || hasStep4Status || hasFinalStatus}
							size={2}
							optional
						/>
						<TextInput
							name="startplaatsBusnummer"
							label="Bus"
							value={model.startplaatsBusnummer}
							onChange={onChangeStartplaats}
							validationMessage={getError("startplaatsBusnummer")}
							validationMessageType={validationMessageType}
							disabled={disabled || hasStep4Status || hasFinalStatus}
							size={2}
							optional
						/>
					</Row>
					<PlaatsPostcodeComponent
						model={model}
						isInBelgie={isStartplaatsInBelgie}
						postcodeField="startplaatsPostcode"
						gemeenteField="startplaatsGemeente"
						postcodeNoBEField="startplaatsPostcodeBuitenland"
						gemeenteNoBEField="startplaatsGemeenteBuitenland"
						postcodes={postcodes}
						gemeenten={gemeenten}
						disabled={disabled || hasStep4Status || hasFinalStatus}
						disableGemeente
						onChange={onChangeStartplaats}
						getError={getError}
						validationMessageType={validationMessageType}
					/>
					{model.startplaatsLand === Landen.BELGIE ?
						<Fragment>
							<BerekenXYComponent
								onGetGeoCoordinates={onGetGeoCoordinatesStart}
								disabled={disabled || hasStep4Status || hasFinalStatus}
								breedtegraadField={"startplaatsBreedtegraad"}
								breedtegraad={model.startplaatsBreedtegraad}
								lengtegraadField="startplaatsLengtegraad"
								lengtegraad={model.startplaatsLengtegraad}
								onChange={onChange}
								errorBreedtegraad={getError("startplaatsBreedtegraad")}
								errorLengtegraad={getError("startplaatsLengtegraad")}
								validationMessageType={validationMessageType}
							/>
							{!(model.startplaatsBreedtegraad && model.startplaatsLengtegraad) ||
								<div id="map_content" className="c-gmap" style={{ margin: '10px', width: 'auto', height: '600px' }}>
									<MapView
										baseLayerUrl={osmBaseLayerUrl}
										mapName={mapId}
										markers={[markerStart]}
										zoom={18}
										center={mapCenter}
										onMarkerDragEnd={onMarkerDragEnd}
										legendeRows={legendeCustomRows}
										manualContent={StartplaatsManualText}
									/>
								</div>
							}
						</Fragment> : null}
				</Fieldset>
				<div id="gemeenten">
					<Fieldset
						title={
							"Gemeente(n) in Vlaanderen waardoor de tocht loopt"
						}
					>
						<ValidationGroup messages={gemErrors ? gemErrors : undefined} validationMessageType="warning">
							{gemeenteComp}
						</ValidationGroup>
					</Fieldset>
				</div>
				<Fieldset title={"Aankomstplaats"}>
					<JaNeeRadioButtonGroup
						label="Is de aankomstplaats dezelfde plaats als de startplaats?"
						name="aankomstGelijkAanStartPlaats"
						value={model["aankomstGelijkAanStartPlaats"]}
						onChange={onChangeRadio}
						horizontal
						labelInside
						disabled={disabled || hasStep4Status || hasFinalStatus}
						validationMessage={getError("aankomstGelijkAanStartPlaats")}
						validationMessageType={validationMessageType}
					/>
					{adresAankomstPlaats}
				</Fieldset>
				<Fieldset title="Afstanden/Routes" tooltip="Hiermee bedoelen we de namen van de verschillende afstanden/routes die georganiseerd worden. Voorbeeld : Familieroute">
					<ValidationGroup messages={hasOnvolledigeLus ? "Vul op de tab Afstanden voor alle routes de gegevens verder aan." : undefined} validationMessageType="warning">
						{lussen}
					</ValidationGroup >

				</Fieldset>
				{extraInformatieAgentschappen}
			</Form>
		</Fragment >
	);
};

UpdateTochtForm.propTypes = {
	model: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	onChangeRadio: PropTypes.func.isRequired,
	onChangeDeelnamePrijs: PropTypes.func.isRequired,
	onRemoveDeelnamePrijs: PropTypes.func.isRequired,
	onAddDeelnamePrijs: PropTypes.func.isRequired,
	onChangeExtraOndersteuning: PropTypes.func.isRequired,
	onChangeLus: PropTypes.func.isRequired,
	onRemoveLus: PropTypes.func.isRequired,
	onAddGemeente: PropTypes.func.isRequired,
	onChangeStartplaats: PropTypes.func.isRequired,
	onChangeGemeente: PropTypes.func.isRequired,
	onRemoveGemeente: PropTypes.func.isRequired,
	onAddLus: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	onGetGeoCoordinatesStart: PropTypes.func.isRequired,
	getError: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	readonly: PropTypes.bool,
	landen: PropTypes.array,
	postcodes: PropTypes.array,
	gemeenten: PropTypes.array,
	hoofdgemeenten: PropTypes.array,
	validationMessageType: PropTypes.string,
	showAnbPopup: PropTypes.bool.isRequired,
	onConfirmAnbPopup: PropTypes.func.isRequired,
	osmBaseLayerUrl: PropTypes.string,
	onMarkerDragEnd: PropTypes.func,
	mapId: PropTypes.string,
	draaiboekFileTypes: PropTypes.object.isRequired,
	color: PropTypes.string.isRequired,
	onDrop: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	fileComponents: PropTypes.object.isRequired,
	downloadFileUrl: PropTypes.string.isRequired
};

export default UpdateTochtForm;
